<template>
  <div>
    <div class="alert-ta-wrap relative w-full h-64 bg-gray-200 rounded-lg">
      <!-- Mirror behind the textarea: transparent text, highlights chars past the limit -->
      <div
        ref="mirror"
        class="alert-ta-shared alert-ta-mirror absolute inset-0"
        aria-hidden="true"
        translate="no"
      >{{ withinLimit }}<mark class="alert-ta-overflow">{{ overLimit }}</mark>{{ "​" }}</div>
      <textarea
        ref="textarea"
        class="alert-ta-shared alert-ta-input relative w-full h-full"
        data-gramm="false"
        :value="modelValue ?? ''"
        :placeholder="placeholder"
        @input="onInput"
        @compositionend="onInput"
        @scroll="syncScroll"
        @blur="emit('blur')"
      />
    </div>
    <p v-if="removedChars.length > 0" class="mt-1 text-sm text-orange-600">
      Removed characters not supported in SMS: {{ removedChars.join(" ") }}
    </p>
    <p v-if="isAtMax" class="mt-1 text-sm text-red-700">
      Maximum length reached: alert messages are limited to {{ maxLength }} characters
      ({{ GSM_MAX_PARTS }} SMS parts).
    </p>
    <AlertCharCounter :message="modelValue" :limit="limit" />
  </div>
</template>

<script setup lang="ts">
import {
  GSM_MAX_PARTS,
  gsmCharWeight,
  gsmLength,
  gsmMaxCharsForParts,
  gsmTruncate,
  toGsm,
} from "../helpers/gsm";

const props = withDefaults(
  defineProps<{
    modelValue: string | null | undefined;
    /** Soft warning limit (highlight + counter turns orange) */
    limit?: number;
    /** Hard limit - no text entry beyond this */
    maxLength?: number;
    placeholder?: string;
  }>(),
  {
    limit: gsmMaxCharsForParts(3),
    maxLength: gsmMaxCharsForParts(GSM_MAX_PARTS),
    placeholder: "",
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "blur"): void;
}>();

const mirror = ref<HTMLDivElement | null>(null);
const textarea = ref<HTMLTextAreaElement | null>(null);
const removedChars = ref<string[]>([]);
const isAtMax = ref(false);

// Index of the first char that pushes the GSM count (extension chars = 2) past the limit
const splitIndex = computed(() => {
  const text = props.modelValue ?? "";
  let count = 0;
  for (let i = 0; i < text.length; i++) {
    count += gsmCharWeight(text[i]);
    if (count > props.limit) return i;
  }
  return text.length;
});
const withinLimit = computed(() => (props.modelValue ?? "").slice(0, splitIndex.value));
const overLimit = computed(() => (props.modelValue ?? "").slice(splitIndex.value));

function onInput(event: Event) {
  // Let IME composition finish before cleaning; compositionend re-runs this
  if ((event as InputEvent).isComposing) return;

  const el = event.target as HTMLTextAreaElement;
  const raw = el.value;
  let { text, removed } = toGsm(raw);
  // Keep the caret where the user was typing after replacements/removals
  let caret = toGsm(raw.slice(0, el.selectionStart ?? raw.length)).text.length;

  isAtMax.value = gsmLength(text) >= props.maxLength;
  if (gsmLength(text) > props.maxLength) {
    ({ text, caret } = trimInsertion(props.modelValue ?? "", text, caret));
  }

  if (text !== raw) {
    el.value = text;
    el.setSelectionRange(caret, caret);
  }

  removedChars.value = removed;
  emit("update:modelValue", text);
  nextTick(syncScroll);
}

// Over the hard max: keep existing text and trim only what was just typed/pasted
function trimInsertion(previous: string, text: string, caret: number) {
  let start = 0;
  while (start < previous.length && start < text.length && previous[start] === text[start])
    start++;
  let end = 0;
  while (
    end < previous.length - start &&
    end < text.length - start &&
    previous[previous.length - 1 - end] === text[text.length - 1 - end]
  )
    end++;

  const prefix = text.slice(0, start);
  const suffix = text.slice(text.length - end);
  const budget = props.maxLength - gsmLength(prefix) - gsmLength(suffix);
  if (budget < 0) {
    // Previous value was already over the max (shouldn't happen) - just cut the end
    const truncated = gsmTruncate(text, props.maxLength);
    return { text: truncated, caret: Math.min(caret, truncated.length) };
  }

  const kept = gsmTruncate(text.slice(start, text.length - end), budget);
  return { text: prefix + kept + suffix, caret: prefix.length + kept.length };
}

function syncScroll() {
  if (!mirror.value || !textarea.value) return;
  mirror.value.scrollTop = textarea.value.scrollTop;
  mirror.value.scrollLeft = textarea.value.scrollLeft;
}

// Value can change without input (template selection, reset after send)
watch(
  () => props.modelValue,
  (value) => {
    const { text: gsmText, removed } = toGsm(value);
    const text = gsmTruncate(gsmText, props.maxLength);
    isAtMax.value = gsmLength(text) >= props.maxLength;
    if (value && text !== value) {
      removedChars.value = removed;
      emit("update:modelValue", text);
    }
    nextTick(syncScroll);
  },
  { immediate: true }
);
</script>

<style scoped>
.alert-ta-wrap {
  color-scheme: light;
}

/* Both layers must share identical box + text metrics so wrapping matches */
.alert-ta-shared {
  box-sizing: border-box;
  margin: 0;
  padding: 0.75rem 1rem;
  border: 1px solid transparent;
  border-radius: 0.5rem;
  font: inherit;
  line-height: 1.5;
  letter-spacing: normal;
  word-spacing: normal;
  text-indent: 0;
  text-transform: none;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  word-break: normal;
  tab-size: 4;
  /* Always reserve the scrollbar gutter so classic (Windows/Linux) scrollbars don't change wrap width */
  overflow-y: scroll;
  overflow-x: hidden;
}

.alert-ta-mirror {
  color: transparent;
  pointer-events: none;
  user-select: none;
  scrollbar-color: transparent transparent;
}

.alert-ta-mirror::-webkit-scrollbar {
  background: transparent;
}

.alert-ta-mirror::-webkit-scrollbar-thumb,
.alert-ta-mirror::-webkit-scrollbar-track {
  background: transparent;
}

.alert-ta-input {
  background: transparent;
  border-color: #e5e7eb;
  resize: none;
  -webkit-appearance: none;
  appearance: none;
  overscroll-behavior: none;
  outline: none;
}

.alert-ta-overflow {
  background-color: #fed7aa;
  color: transparent;
  border-radius: 2px;
  padding: 0;
}
</style>
